import { useSocketContext } from "@/contexts/socketioContext";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { __GetContentTypeConfigNew } from "@/utils/getters/gettersV2";
import {
  ButtonGhostCompact,
  colors2,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_CAPTION_DF,
  UI_CAPTION_SEMIBOLD_DF,
  TooltipV2,
  icons,
  LoaderCircleV2,
} from "oolib";
import React, { Fragment, useMemo, useState } from "react";
import { RotatingCaret } from "../generalUI/RotatingCaret";
import { buildSyntheticSegByAutoAnnotateIdJobs } from "./AutoAnnotateProgressFeedbackTester";

const genIcon = ({ icon, size, color, weight }) => {
  const Icon = icons[icon];
  if (!Icon) return null;
  return <Icon size={size} color={color} weight={weight} />;
};

// Per-doc row in the expanded list: status icon + title (or id fallback).
const DocRow = ({ doc, status, onSecondaryColor }) => {
  const display = doc?.title || doc?._id || "(unknown doc)";

  let iconEl;
  let textColor = colors2.grey90;
  if (status === "completed") {
    iconEl = genIcon({ icon: "CheckCircle", color: colors2.green, size: 16, weight: "bold" });
  } else if (status === "failed") {
    iconEl = genIcon({ icon: "XCircle", color: colors2.red, size: 16, weight: "bold" });
    textColor = colors2.redText || colors2.red;
  } else {
    // diameter:14 — sits a touch under the 16px CheckCircle/XCircle so the
    // spinner reads as visually equivalent rather than slightly heavier
    // (the loader's full circular stroke makes it feel denser at equal px).
    // isBlock:false avoids the default 100%-height grid wrapper.
    iconEl = <LoaderCircleV2 diameter={14} isBlock={false} color={onSecondaryColor} />;
    textColor = colors2.grey60;
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "4px 0",
        minWidth: 0,
      }}
    >
      <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>
        {iconEl}
      </div>
      <UI_CAPTION_DF
        style={{
          color: textColor,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
          flex: 1,
        }}
      >
        {display}
      </UI_CAPTION_DF>
    </div>
  );
};

// "Doc A, Doc B & 12 other docs" — baked into the completed-state header.
const buildCompletedSummary = (completedDocs) => {
  if (!completedDocs || completedDocs.length === 0) return null;
  const titles = completedDocs.map((d) => d?.title || d?._id || "Untitled");
  if (titles.length === 1) return titles[0];
  if (titles.length === 2) return `${titles[0]} & ${titles[1]}`;
  const headCount = 2;
  const head = titles.slice(0, headCount).join(", ");
  const rest = titles.length - headCount;
  return `${head} & ${rest} other doc${rest === 1 ? "" : "s"}`;
};

const SingleRunCard = ({ runData, _Theme, isTestMode }) => {
  const onSecondaryColor = _Theme?.colors?.onSecondary;
  const secondaryContainerColor = _Theme?.colors?.secondaryContainer;

  const { autoAnnotateId, contentType, queues, docs, _testTplTitle } = runData;
  const queueData = Object.values(queues || {})[0];

  // _testTplTitle is embedded by the tester for per-card overrides; otherwise
  // fall back to the real tpl-cache lookup.
  const tplTitle =
    _testTplTitle ??
    __GetContentTypeConfigNew(contentType)?.general?.content?.title;
  const tplTitleSuffix = tplTitle ? ` — ${tplTitle}` : "";

  const isCompleted = queueData?.status === "autoAnnotateCompleted";
  const percent = queueData?.actualPercentCompleted ?? 0;
  const completedPctDisplay = Math.round(queueData?.completed?.percent ?? 0);
  const failedPctDisplay = Math.round(queueData?.failed?.percent ?? 0);
  const failedCount = queueData?.failed?.count ?? 0;
  const hasFailures = failedCount > 0;

  // Per-doc lists (option-A: batch granularity, ~5 docs flip at once).
  // Degrades gracefully when BE didn't send `docs` (legacy runs in flight
  // when this change shipped) — expand button is suppressed.
  const completedDocs = docs?.completed || [];
  const failedDocs = docs?.failed || [];
  const pendingDocs = docs?.pending || [];
  const hasDocs = completedDocs.length + failedDocs.length + pendingDocs.length > 0;

  const [expanded, setExpanded] = useState(false);

  // Completed-state docs summary, baked into the header label as a " : Doc A,
  // Doc B & N other docs" suffix. Null when there are no completed docs (e.g.
  // all-failures case) — header then stays as just "Annotated[, with failures]
  // — <tpl>".
  const completedSummary = isCompleted ? buildCompletedSummary(completedDocs) : null;
  const completedSummarySuffix = completedSummary ? ` : ${completedSummary}` : "";

  // Header label + icon + color
  let headerLabel;
  let headerColor;
  let headerIcon;
  let useLoader = false;
  let tooltipText;

  if (isCompleted && hasFailures) {
    headerIcon = "Warning";
    headerColor = colors2.redText || colors2.red;
    headerLabel = `Annotated, with failures${tplTitleSuffix}${completedSummarySuffix}`;
    tooltipText = `${completedPctDisplay}% Annotated, ${failedPctDisplay}% Failed${tplTitleSuffix}${completedSummarySuffix}`;
  } else if (isCompleted) {
    headerIcon = "CheckCircle";
    headerColor = colors2.green;
    headerLabel = `Annotated${tplTitleSuffix}${completedSummarySuffix}`;
  } else {
    useLoader = true;
    headerColor = onSecondaryColor;
    headerLabel = tplTitle ? `Annotating ${tplTitle}` : `Annotating data`;
    if (hasFailures) {
      tooltipText = `${completedPctDisplay}% Annotated, ${failedPctDisplay}% Failed${tplTitleSuffix}`;
    }
  }

  // Suppress ones the user has already closed (per-run id, same pattern as
  // the import lightbox). Bypassed in test mode so toggling states works.
  const closedKey = "CLOSED_autoAnnotateProgressFeedbackIds";
  const closedRaw =
    typeof window !== "undefined" ? localStorage.getItem(closedKey) : null;
  const closedIds = closedRaw ? closedRaw.split(",") : [];
  const [hidden, setHidden] = useState(
    !isTestMode && closedIds.includes(autoAnnotateId)
  );
  if (hidden) return null;

  const handleClose = (e) => {
    e.stopPropagation();
    if (!isTestMode) {
      const next = Array.from(new Set([...closedIds, autoAnnotateId])).slice(-50);
      localStorage.setItem(closedKey, next.join(","));
    }
    setHidden(true);
  };

  // Expand only meaningful when we have a per-doc list to show.
  const showExpandButton = hasDocs;

  return (
    <div
      key={autoAnnotateId}
      style={{
        minWidth: "350px",
        maxWidth: "450px",
        boxShadow: `0px 4px 20px 0px ${colors2.grey15}`,
        border: `1px solid ${colors2.grey10}`,
        borderRadius: "6px",
        background: colors2.white,
      }}
    >
      <div style={{ padding: "16px", paddingBottom: expanded ? "8px" : "16px" }}>
        <button
          type="button"
          onClick={() => showExpandButton && setExpanded((p) => !p)}
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "6px",
            border: "none",
            background: "unset",
            padding: 0,
            cursor: showExpandButton ? "pointer" : "default",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              minWidth: 0,
              flex: 1,
            }}
          >
            <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>
              {useLoader
                ? <LoaderCircleV2 S color={headerColor} />
                : genIcon({ icon: headerIcon, color: headerColor, size: 20, weight: "bold" })}
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <TooltipV2 text={tooltipText || headerLabel} placement="top" wrapperDisplay="block">
                <UI_BODY_SEMIBOLD_SM_DF
                  style={{
                    color: headerColor,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                  }}
                >
                  {headerLabel}
                </UI_BODY_SEMIBOLD_SM_DF>
              </TooltipV2>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              color: onSecondaryColor,
              flex: "0 0 auto",
            }}
          >
            {!isCompleted && (
              <UI_BODY_SEMIBOLD_SM_DF>{`${Math.round(percent)}%`}</UI_BODY_SEMIBOLD_SM_DF>
            )}
            {showExpandButton && (
              <RotatingCaret
                color={colors2.grey80}
                iconSize={16}
                position={expanded ? "up" : "down"}
              />
            )}
            {isCompleted && (
              <ButtonGhostCompact onClick={handleClose} icon="X" />
            )}
          </div>
        </button>

        {/* progress bar — shown during run, AND after settle when failures
            exist so the red segment stays visible */}
        {(!isCompleted || hasFailures) && (
          <div
            style={{
              display: "flex",
              width: "100%",
              marginTop: "8px",
              gap: "2px",
            }}
          >
            <div
              style={{
                flex: hasFailures
                  ? `0 0 ${100 - failedPctDisplay}%`
                  : "1 1 auto",
                position: "relative",
                height: "8px",
                backgroundColor: secondaryContainerColor,
                borderRadius: "14px",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: `${Math.round(percent)}%`,
                  backgroundColor: onSecondaryColor,
                  borderRadius: "14px",
                }}
              />
            </div>
            {hasFailures && (
              <div
                style={{
                  flex: `0 0 ${failedPctDisplay}%`,
                  height: "8px",
                  backgroundColor: colors2.red,
                  borderRadius: "14px",
                }}
              />
            )}
          </div>
        )}

      </div>

      {/* Expanded: full per-doc list (failed first, then pending, then completed
          — most-actionable-first reading order) */}
      {expanded && hasDocs && (
        <div
          style={{
            borderTop: `1px solid ${colors2.grey10}`,
            padding: "12px 16px",
            maxHeight: "240px",
            overflowY: "auto",
          }}
        >
          {failedDocs.length > 0 && (
            <Fragment>
              <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey60, marginBottom: "4px" }}>
                Failed ({failedDocs.length})
              </UI_CAPTION_SEMIBOLD_DF>
              {failedDocs.map((d) => (
                <DocRow key={`f-${d?._id}`} doc={d} status="failed" onSecondaryColor={onSecondaryColor} />
              ))}
            </Fragment>
          )}
          {pendingDocs.length > 0 && (
            <Fragment>
              <UI_CAPTION_SEMIBOLD_DF
                style={{
                  color: colors2.grey60,
                  marginBottom: "4px",
                  marginTop: failedDocs.length > 0 ? "12px" : 0,
                }}
              >
                Pending ({pendingDocs.length})
              </UI_CAPTION_SEMIBOLD_DF>
              {pendingDocs.map((d) => (
                <DocRow key={`p-${d?._id}`} doc={d} status="pending" onSecondaryColor={onSecondaryColor} />
              ))}
            </Fragment>
          )}
          {completedDocs.length > 0 && (
            <Fragment>
              <UI_CAPTION_SEMIBOLD_DF
                style={{
                  color: colors2.grey60,
                  marginBottom: "4px",
                  marginTop: failedDocs.length + pendingDocs.length > 0 ? "12px" : 0,
                }}
              >
                Annotated ({completedDocs.length})
              </UI_CAPTION_SEMIBOLD_DF>
              {completedDocs.map((d) => (
                <DocRow key={`c-${d?._id}`} doc={d} status="completed" onSecondaryColor={onSecondaryColor} />
              ))}
            </Fragment>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * `testMode` (optional): when provided, drives the component into a synthetic
 * state without needing a real auto-annotate run. Single run or array of runs.
 *
 *   <AutoAnnotateProgressFeedback
 *     testMode={{ contentType: "anything", state: "annotating", tplTitle: "Articles" }}
 *   />
 *
 * Shape:
 *   - contentType: any string (need NOT be a real tpl id)
 *   - state: "annotating" | "annotatingWithFailures" | "completed" | "completedWithFailures"
 *   - tplTitle (optional): free-form string used in the labels. Overrides
 *     the real tpl-cache lookup when set.
 *
 * In test mode, the localStorage close-suppression is bypassed so toggling
 * states / re-running works freely.
 */
export const AutoAnnotateProgressFeedback = ({ testMode } = {}) => {
  const { segByAutoAnnotateIdJobs: realSegByAutoAnnotateIdJobs } =
    useSocketContext() || {};
  const { _Theme } = useGetQueryData("platformConfigs") || {};

  const segByAutoAnnotateIdJobs = testMode
    ? buildSyntheticSegByAutoAnnotateIdJobs(testMode)
    : realSegByAutoAnnotateIdJobs;

  const runs = useMemo(() => {
    if (!segByAutoAnnotateIdJobs) return [];
    return Object.values(segByAutoAnnotateIdJobs);
  }, [segByAutoAnnotateIdJobs]);

  if (runs.length === 0) return null;

  // Fixed positioning lives on the parent stack container in App.jsx — this
  // component just emits its cards as flex children.
  return (
    <Fragment>
      {runs.map((runData) => (
        <SingleRunCard
          key={runData.autoAnnotateId}
          runData={runData}
          _Theme={_Theme}
          isTestMode={!!testMode}
        />
      ))}
    </Fragment>
  );
};
